import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import AdminNav from "./AdminNav";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/type";
import toast from "react-hot-toast";
import { getSettings, updateSettings } from "../../api/admin";
import { MarginBottom } from "../MarginBottom";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SettingsContainer = styled.div`
  width: 100%;
  background: #121212;
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1rem;
  overflow-x: auto;
`;

const SettingsTitle = styled.h2`
  color: #ccff00;
  margin-bottom: 1.8rem;
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(90deg, #ccff00, #00b4db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #ccff00, #00b4db);
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const SettingsForm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.8rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.2rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.6rem;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.9rem 1.2rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.07);
  color: white;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  &:focus {
    outline: none;
    border-color: #ccff00;
    box-shadow: 0 0 0 3px rgba(0, 224, 255, 0.15);
    background: rgba(0, 224, 255, 0.05);
  }

  &[type="checkbox"] {
    width: auto;
    height: 1.2rem;
    width: 1.2rem;
    accent-color: #ccff00;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
    font-size: 0.9rem;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.9rem 1.2rem;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.07);
  color: white;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;

  &:focus {
    outline: none;
    border-color: #ccff00;
    box-shadow: 0 0 0 3px rgba(0, 224, 255, 0.15);
  }

  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
    font-size: 0.9rem;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.5rem;

  ${FormLabel} {
    margin-bottom: 0;
    cursor: pointer;
  }
`;

const SaveButton = styled.button`
  grid-column: 1 / -1;
  padding: 1rem;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #ccff00, #00b4db);
  color: #111;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 224, 255, 0.25);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to bottom right,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 0.2),
      rgba(255, 255, 255, 0)
    );
    transform: rotate(30deg) translate(-20%, -100%);
    transition: transform 0.6s ease;
  }

  &:hover::after {
    transform: rotate(30deg) translate(20%, 100%);
  }

  @media (max-width: 768px) {
    padding: 0.9rem;
    font-size: 0.95rem;
  }
`;

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    rewardMultiplier: 1.0,
    minWithdrawalAmount: 100,
    maxWithdrawalAmount: 10000,
    referralBonus: 10,
    telegramBotLink: "",
    dailyLifelineTime: 0,
    twitterHandle: "",
    adminEmail: "",
  });
  const [loading, setLoading] = useState(false);
  const user = useSelector((state: RootState) => state.userData);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getSettings(user?.uid); // Assuming you have a function to fetch settings
        if (!data) return;

        setSettings((prev) => ({
          ...prev,
          maintenanceMode: data.maintenanceMode,
          rewardMultiplier: data.rewardMultiplier,
          minWithdrawalAmount: data.minWithdrawalAmount,
          maxWithdrawalAmount: data.maxWithdrawalAmount,
          referralBonus: data.referralBonus,
          telegramBotLink: data.telegramBotLink,
          twitterHandle: data.twitterHandle,
          dailyLifelineTime: data.dailyLifelineTime,
          adminEmail: data.adminEmail,
        }));
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await updateSettings(user?.uid, settings);

      if (data) {
        toast.success("Settings updated successfully!");
      } else {
        toast.error("Failed to update settings. Please try again.");
      }
    } catch (error) {
      console.error("Error updating settings:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    settings && (
      <>
        <SettingsContainer>
          <SettingsTitle>System Settings</SettingsTitle>

          <SettingsForm>
            <CheckboxContainer>
              <FormInput
                type="checkbox"
                id="maintenanceMode"
                name="maintenanceMode"
                checked={settings.maintenanceMode}
                onChange={handleChange}
              />
              <FormLabel htmlFor="maintenanceMode">Maintenance Mode</FormLabel>
            </CheckboxContainer>

            <FormGroup>
              <FormLabel>Telegram Bot Link</FormLabel>
              <FormInput
                type="text"
                name="telegramBotLink"
                value={settings.telegramBotLink}
                onChange={handleChange}
                placeholder="https://t.me/PlayOpsARGTerminalBot"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Official Twitter Handle</FormLabel>
              <FormInput
                type="text"
                name="twitterHandle"
                value={settings.twitterHandle}
                onChange={handleChange}
                placeholder="@yourhandle"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Admin Contact Email</FormLabel>
              <FormInput
                type="email"
                name="adminEmail"
                value={settings.adminEmail}
                onChange={handleChange}
                placeholder="admin@example.com"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Daily Lifeline Time (in minutes)</FormLabel>
              <FormInput
                type="number"
                name="dailyLifelineTime"
                value={settings.dailyLifelineTime}
                onChange={handleChange}
                placeholder="23.25"
              />
            </FormGroup>

            <SaveButton onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : "Save Settings"}
            </SaveButton>
          </SettingsForm>
        </SettingsContainer>
        <MarginBottom />
      </>
    )
  );
};

export default AdminSettings;
